import {
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  SvgIcon,
  SvgIconProps,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  fade,
  makeStyles
} from '@material-ui/core/styles';
import { ClearRounded, LaunchRounded, UpdateRounded } from '@material-ui/icons';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RevisionNoteAndLink } from '../../api/project-document-revisions';
import checkWebUrl from '../../functions/checkWebUrl';
import { useAppDispatch } from '../../hooks/useAppRudux';
import loadingSlice from '../../pages/Loading/loadingSlice';
import {
  getProjectDocumentRevisionAsync,
  updateRevisionNoteAndLinkAsync
} from '../../pages/ProjectDocumentRevision/projectDocumentRevisionSlice';

interface UploadLinkProps {
  open: boolean;
  onClose: () => void;
  linkName: string | null;
  linkUrl: string | null;
  documentRevisionId: number;
}

export default function UploadLink({
  open,
  onClose,
  linkName,
  linkUrl,
  documentRevisionId: id
}: UploadLinkProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [link, setLink] = useState<{
    linkName: string | null;
    linkUrl: string | null;
  }>({ linkName: null, linkUrl: null });
  const [newLink, setNewLink] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setLink({ ...link, linkName, linkUrl });
      setNewLink(!Boolean(linkUrl));
    } else {
      setTimeout(() => {
        setLink({ ...link, linkName: null, linkUrl: null });
      }, 200);
    }
  }, [open]);

  async function onRemoveLink() {
    try {
      setLoading(true);
      const updateRevisionNoteAndLink: RevisionNoteAndLink = {
        id,
        linkName: null,
        linkUrl: null
      };
      await dispatch(
        updateRevisionNoteAndLinkAsync(updateRevisionNoteAndLink)
      ).unwrap();
      await dispatch(getProjectDocumentRevisionAsync(id)).unwrap();
      dispatch(
        loadingSlice.actions.success({
          message: t('uploadLink.linkRemoved') as string
        })
      );
      onClose();
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  async function onSubmit(
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();
    try {
      setLoading(true);
      const updateRevisionNoteAndLink: RevisionNoteAndLink = {
        id,
        linkName: link.linkName,
        linkUrl: link.linkUrl
      };
      await dispatch(
        updateRevisionNoteAndLinkAsync(updateRevisionNoteAndLink)
      ).unwrap();
      await dispatch(getProjectDocumentRevisionAsync(id)).unwrap();
      dispatch(
        loadingSlice.actions.success({
          message: newLink ? t('uploadLink.linkAdded') as string : t('uploadLink.updated') as string
        })
      );
      onClose();
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <div className={classes.dialogTitle}>
        <Typography className={classes.flexGrow} variant="h6">
          {t('uploadLink.attachLink') as string}
        </Typography>
        <Tooltip title={t('uploadLink.close') as string} arrow>
          <IconButton size="small" onClick={onClose}>
            <ClearRounded />
          </IconButton>
        </Tooltip>
      </div>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={link.linkUrl ? 8 : 12}>
              <TextField
                id="link-url"
                label={t('uploadLink.websiteUrl') as string}
                variant="outlined"
                size="small"
                type="url"
                value={link.linkUrl ? link.linkUrl : ''}
                onChange={(event) => {
                  const linkUrl = event.target.value;
                  setLink({
                    ...link,
                    linkUrl: linkUrl === '' ? null : linkUrl,
                    linkName: linkUrl === '' ? null : link.linkName
                  });
                }}
                fullWidth
                required={Boolean(link.linkName)}
              />
            </Grid>
            {link.linkUrl && (
              <Grid item xs={12} sm={4}>
                <TextField
                  id="link-name"
                  label={t('uploadLink.linkName') as string}
                  variant="outlined"
                  size="small"
                  value={link.linkName ? link.linkName : ''}
                  onChange={(event) => {
                    setLink({
                      ...link,
                      linkName:
                        event.target.value === '' ? null : event.target.value
                    });
                  }}
                  fullWidth
                />
              </Grid>
            )}
            {link.linkUrl && (
              <Grid className={classes.linkBox} item xs={12}>
                <Typography
                  className={clsx(classes.link, classes.marginX)}
                  component="a"
                  href={checkWebUrl(link.linkUrl)}
                  target="_blank"
                  variant="body1"
                  display="inline"
                >
                  {link.linkName ? link.linkName : link.linkUrl}
                  <LaunchRounded color="action" fontSize="small" />
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <div className={classes.dialogAction}>
          {!newLink && (
            <Button
              variant="outlined"
              fullWidth
              disabled={loading}
              onClick={onRemoveLink}
            >
              {t('uploadLink.removeLink') as string}
            </Button>
          )}
          <Button
            type="submit"
            color="primary"
            variant="contained"
            fullWidth
            startIcon={newLink ? <AddLinkRounded /> : <UpdateRounded />}
            disabled={!Boolean(link.linkUrl) || loading}
          >
            {newLink ? t('uploadLink.addNewLink') as string : t('uploadLink.update') as string}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function AddLinkRounded(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <path d="M9 11h6c.55 0 1 .45 1 1s-.45 1-1 1H9c-.55 0-1-.45-1-1s.45-1 1-1zm11.93 1c.62 0 1.07-.59.93-1.19C21.32 8.62 19.35 7 17 7h-3.05c-.52 0-.95.43-.95.95s.43.95.95.95H17c1.45 0 2.67 1 3.01 2.34.11.44.47.76.92.76zm-16.97-.62C4.24 9.91 5.62 8.9 7.12 8.9h2.93c.52 0 .95-.43.95-.95S10.57 7 10.05 7H7.22c-2.61 0-4.94 1.91-5.19 4.51C1.74 14.49 4.08 17 7 17h3.05c.52 0 .95-.43.95-.95s-.43-.95-.95-.95H7c-1.91 0-3.42-1.74-3.04-3.72zM18 12c-.55 0-1 .45-1 1v2h-2c-.55 0-1 .45-1 1s.45 1 1 1h2v2c0 .55.45 1 1 1s1-.45 1-1v-2h2c.55 0 1-.45 1-1s-.45-1-1-1h-2v-2c0-.55-.45-1-1-1z" />
    </SvgIcon>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2, 2, 1, 3)
    },
    dialogAction: {
      padding: theme.spacing(1),
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(1),
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column-reverse'
      }
    },
    flexGrow: {
      flexGrow: 1
    },
    marginX: {
      margin: theme.spacing(0, 1)
    },
    link: {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      cursor: 'pointer',
      '&:hover': {
        color: theme.palette.primary.dark
      },
      wordBreak: 'break-all'
    },
    linkBox: {
      borderRadius: theme.spacing(1),
      borderColor: '#dddddd',
      backgroundColor: fade(theme.palette.background.default, 0.75),
      margin: theme.spacing(0.8, 1)
    }
  })
);
